import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { BackLink, PageHeader } from '@/layouts/AppLayout';

const schema = z
  .object({
    hotelRoomTypeId: z.string().uuid('Select a room type'),
    mealPlan: z.string().min(1, 'Meal plan is required').max(20),
    occupancy: z.enum(['single', 'double', 'triple', 'child']),
    rate: z.coerce.number().positive('Rate must be positive'),
    validFrom: z.string().min(1, 'Valid from is required'),
    validTo: z.string().min(1, 'Valid to is required'),
    notes: z.string().optional(),
  })
  .refine((d) => new Date(d.validTo) >= new Date(d.validFrom), {
    message: 'Valid to must be on or after valid from',
    path: ['validTo'],
  });

type FormValues = z.infer<typeof schema>;

interface Rate {
  id: string;
  hotelRoomType: { id: string; name: string };
  mealPlan: string;
  occupancy: string;
  rate: string;
  currency: string;
  validFrom: string;
  validTo: string;
  notes: string | null;
  hotel: { id: string; name: string };
  client: { id: string; name: string };
}

interface RoomType { id: string; name: string; code: string | null }

const MEAL_PLANS = ['CP', 'MAP', 'AP', 'APAI', 'MAPAI', 'CPI', 'EP'];

function toDateInput(isoStr: string) {
  return isoStr.slice(0, 10);
}

export function EditRatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: rate, isLoading } = useQuery({
    queryKey: ['contracted-rates', id],
    queryFn: () => api.get<Rate>(`/contracted-rates/${id}`),
    enabled: !!id,
  });

  const { data: roomTypes = [] } = useQuery({
    queryKey: ['hotels', rate?.hotel.id, 'room-types'],
    queryFn: () => api.get<RoomType[]>(`/hotels/${rate!.hotel.id}/room-types`),
    enabled: !!rate?.hotel.id,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: rate
      ? {
          hotelRoomTypeId: rate.hotelRoomType.id,
          mealPlan: rate.mealPlan,
          occupancy: rate.occupancy as FormValues['occupancy'],
          rate: Number(rate.rate),
          validFrom: toDateInput(rate.validFrom),
          validTo: toDateInput(rate.validTo),
          notes: rate.notes ?? undefined,
        }
      : undefined,
  });

  const hotelRoomTypeId = watch('hotelRoomTypeId');
  const occupancy = watch('occupancy');
  const mealPlan = watch('mealPlan');

  const mutation = useMutation({
    mutationFn: (data: FormValues) =>
      api.patch(`/contracted-rates/${id}`, { ...data, rate: String(data.rate) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracted-rates'] });
      toast.success('Rate updated');
      navigate('/rates');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to update rate';
      setError('root', { message: msg });
    },
  });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>;
  if (!rate) return <p className="p-6 text-danger-700">Rate not found.</p>;

  return (
    <div>
      <PageHeader title={`Edit rate — ${rate.hotel.name} × ${rate.client.name}`} />
      <div className="p-6 max-w-2xl">
        <BackLink to="/rates" label="Back to rates" />
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Rate details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Room type *</Label>
                  <Select
                    value={hotelRoomTypeId ?? ''}
                    onValueChange={(v) => setValue('hotelRoomTypeId', v, { shouldValidate: true, shouldDirty: true })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room type…" />
                    </SelectTrigger>
                    <SelectContent>
                      {roomTypes.map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>
                          {rt.name}{rt.code ? ` (${rt.code})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.hotelRoomTypeId && (
                    <p className="text-xs text-danger-700">{errors.hotelRoomTypeId.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Meal plan *</Label>
                  <Select
                    value={mealPlan}
                    onValueChange={(v) => setValue('mealPlan', v, { shouldDirty: true })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEAL_PLANS.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.mealPlan && <p className="text-xs text-danger-700">{errors.mealPlan.message}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Occupancy</Label>
                  <Select
                    value={occupancy}
                    onValueChange={(v) => setValue('occupancy', v as FormValues['occupancy'], { shouldDirty: true })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="double">Double</SelectItem>
                      <SelectItem value="triple">Triple</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rate">Rate (₹) *</Label>
                  <Input id="rate" type="number" min={0} step="0.01" {...register('rate')} />
                  {errors.rate && <p className="text-xs text-danger-700">{errors.rate.message}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="validFrom">Valid from *</Label>
                  <Input id="validFrom" type="date" {...register('validFrom')} />
                  {errors.validFrom && <p className="text-xs text-danger-700">{errors.validFrom.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="validTo">Valid to *</Label>
                  <Input id="validTo" type="date" {...register('validTo')} />
                  {errors.validTo && <p className="text-xs text-danger-700">{errors.validTo.message}</p>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" {...register('notes')} />
              </div>
            </CardContent>
          </Card>

          {errors.root && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{errors.root.message}</p>
          )}
          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/rates')}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
